import { NetworkStatus, gql, useQuery } from '@apollo/client';
import {
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  LinkBox,
  LinkOverlay,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyCard } from '@/components/EmptyCard';
import { Sentinel } from '@/components/Sentinel';
import { ListItem } from '@/components/groups/ListItem';
import { Groups } from '@/components/placeholder-feed/Groups';
import { useEditCurrentUserGroupMembership } from '@/hooks/useEditCurrentUserGroupMembership/useEditCurrentUserGroupMembership';
import { logError } from '@/imports/logging/ClientLogger';
import { Card } from '@/imports/ui/chakra/components/Card';
import { SearchIcon } from '@/imports/ui/chakra/feather';
import { useDebouncedValue } from '@/imports/ui/hooks/useDebouncedValue';

import {
  MyGroupsPageContent_UserGroupsQuery,
  MyGroupsPageContent_UserGroupsQuery_currentUser_visibleGroupLinks_group,
} from './graphql-types/MyGroupsPageContent_UserGroupsQuery';

const LIMIT = 20;

const USER_GROUPS_QUERY = gql`
  query MyGroupsPageContent_UserGroupsQuery {
    currentUser {
      _id
      visibleGroupLinks {
        _id
        group {
          _id
          name
          totalMembers
          priority
          profileImage {
            _id
            imageProxy(input: { w: 96, h: 96, fit: "fill", fill: "solid" })
          }
          settings {
            communityVisibility
          }
          currentUserGroupLink {
            _id
          }
          subgroups {
            totalCount
          }
          ...useEditCurrentUserGroupMembership_GroupFragment
        }
      }
    }
  }
  ${useEditCurrentUserGroupMembership.groupFragment}
`;

const LeaveButton: React.FC<{
  group: MyGroupsPageContent_UserGroupsQuery_currentUser_visibleGroupLinks_group;
  onLeave: () => Promise<void>;
}> = ({ group, onLeave }) => {
  const { deleteFollow, deleteFollowLoading } =
    useEditCurrentUserGroupMembership(group);
  const confirmDialog = useDisclosure();

  const handleLeave = async (): Promise<void> => {
    await deleteFollow();
    await onLeave();
    confirmDialog.onClose();
  };

  return (
    <>
      <Button
        onClick={confirmDialog.onOpen}
        size="sm"
        colorScheme="gray"
        isLoading={deleteFollowLoading}
      >
        Leave
      </Button>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        onConfirm={handleLeave}
        confirmButtonText="Leave"
        title={`Are you sure you want to leave ${group.name}?`}
        body={
          group.subgroups.totalCount > 0
            ? 'Since this group has subgroups, you will automatically leave all its subgroups too.'
            : `If you leave, you'll lose access to this group's discussions, events, and resources. You'll no longer receive updates or notifications from this group.`
        }
      />
    </>
  );
};

export const MyGroupsPageContent: React.FC = () => {
  const { error, networkStatus, data, refetch } =
    useQuery<MyGroupsPageContent_UserGroupsQuery>(USER_GROUPS_QUERY, {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });

  const groups = data?.currentUser?.visibleGroupLinks || [];
  const groupsList = groups.map((link) => link.group);

  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebouncedValue(search, 500);

  const filteredGroups = React.useMemo(() => {
    if (!debouncedSearch) return groupsList;
    return groupsList.filter((group) =>
      group.name.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [groupsList, debouncedSearch]);

  if (error) {
    logError(error);
  }

  const loading =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables;

  const handleRefetch = async (): Promise<void> => {
    await refetch();
  };

  return (
    <VStack>
      {/* <Card w="full" px="4" py="6">
        Groups that you have joined
      </Card> */}
      <Card w="full" px="4" py="4">
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.600" />
          </InputLeftElement>
          <Input
            variant="filled"
            borderRadius="full"
            placeholder="Search your groups"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </InputGroup>
      </Card>
      {loading ? (
        <Groups w="full" />
      ) : filteredGroups.length > 0 ? (
        <>
          {filteredGroups.map((group) => (
            <Card w="full" key={group._id}>
              <LinkBox w="full">
                <HStack w="full" justifyContent="space-between" px="4" py="4">
                  <LinkOverlay
                    as={NextLink}
                    href={`/community/groups/${group._id}/activity`}
                  >
                    <ListItem group={group} showHiddenTag={false} />
                  </LinkOverlay>
                  <HStack pr="10">
                    <LeaveButton group={group} onLeave={handleRefetch} />
                  </HStack>
                </HStack>
              </LinkBox>
            </Card>
          ))}
        </>
      ) : (
        <EmptyCard
          title="You haven't joined any groups yet"
          subtitle="Explore and join groups to see them here"
        />
      )}
    </VStack>
  );
};
